import React from 'react'
import styled from 'styled-components'
import dayjs from 'dayjs'
import advancedFormat from 'dayjs/plugin/advancedFormat'
import { range } from 'utils/commonUtils'
import { OptionItem } from '../common'
import DrumPicker from './DrumPicker'
import AnimationModal from 'components/elements/modal/AnimationModal'
import {
  TROUBLE_DATE_FORMAT,
  TROUBLE_DATE_DISPLAY_FORMAT,
} from '../../../constants/qnState'

// 変形フォーマットプラグインを追加
dayjs.extend(advancedFormat)

export type TimePickerProps = {
  /** 時間(YYYY/MM/DD:HH) */
  time: string
  /** 選択可能な時間の最小値(YYYY/MM/DD:HH) */
  minTime?: string
  /** 値が変更された時のコールバック */
  onChanged?: (time: string) => void
}

/** 年の選択肢 */
const yearOptions: OptionItem<number>[] = range(
  2000,
  Number(dayjs().format('YYYY'))
).map((value) => ({
  label: String(value).padStart(2, '0'),
  value,
}))
/** 月の選択肢 */
const monthOptions: OptionItem<number>[] = range(1, 12).map((value) => ({
  label: String(value).padStart(2, '0'),
  value,
}))
/** 日の選択肢 */
const dateOptions: OptionItem<number>[] = range(1, 31).map((value) => ({
  label: String(value).padStart(2, '0'),
  value,
}))
/** 時の選択肢 */
const hourOptions: OptionItem<number>[] = range(0, 23).map((value) => ({
  label: String(value).padStart(2, '0'),
  value,
}))

/** 時間を選択するコンポーネント */
const DatePicker = (props: TimePickerProps): JSX.Element => {
  const { time, minTime, onChanged } = props
  const [isPickerOpened, setIsPickerOpened] = React.useState(false)

  const [yearItems, setYearItems] = React.useState<OptionItem<number>[]>([])
  const [monthItems, setMonthItems] = React.useState<OptionItem<number>[]>([])
  const [dateItems, setDateItems] = React.useState<OptionItem<number>[]>([])
  const [hourItems, setHourItems] = React.useState<OptionItem<number>[]>([])

  // 年月日時間
  const [year, month, date, hour] = React.useMemo(() => {
    const djTime = dayjs(time)
    return [
      djTime.year() ? djTime.year() : yearOptions[0].value,
      djTime.month() ? djTime.month() + 1 : monthOptions[0].value,
      djTime.date() ? djTime.date() : dateOptions[0].value,
      djTime.hour() ? djTime.hour() : hourOptions[0].value,
    ]
  }, [time])

  const [selectedYear, setSelectedYear] = React.useState(year)
  const [selectedMonth, setSelectedMonth] = React.useState(month)
  const [selectedDate, setSelectedDate] = React.useState(date)
  const [selectedHour, setSelectedHour] = React.useState(hour)

  // 最小時間
  const [minYear, minMonth, minDate, minHour] = React.useMemo(() => {
    const djTime = minTime ? dayjs(minTime) : undefined
    return djTime
      ? [djTime.year(), djTime.month() + 1, djTime.date(), djTime.hour()]
      : [
          yearOptions[0].value,
          monthOptions[0].value,
          dateOptions[0].value,
          hourOptions[0].value,
        ]
  }, [minTime])

  /** トラブル発生日時を押下したときのイベント */
  const handleDateClick = React.useCallback(
    (value?: boolean) => {
      // ドラムの選択肢を設定
      setYearItems(yearOptions.filter((x) => x.value >= minYear))
      setMonthItems(monthOptions.filter((x) => x.value >= minMonth))
      setDateItems(dateOptions.filter((x) => x.value >= minDate))
      setHourItems(hourOptions.filter((x) => x.value >= minHour))
      // ドラムの選択値を設定
      setSelectedYear(selectedYear <= minYear ? minYear : selectedYear)
      setSelectedMonth(selectedMonth <= minMonth ? minMonth : selectedMonth)
      setSelectedDate(selectedDate <= minDate ? minDate : selectedDate)
      setSelectedHour(selectedHour <= minHour ? minHour : selectedHour)
      // モーダル表示
      value && setIsPickerOpened(true)
    },
    [
      year,
      month,
      date,
      hour,
      minYear,
      minMonth,
      minDate,
      minHour,
      setYearItems,
      setMonthItems,
      setDateItems,
      setHourItems,
    ]
  )

  // バリデーション
  const validateCheck = (date: string, format: string): boolean => {
    // 存在する日付であること && 未来の日付ではないこと
    return (
      dayjs(date, format).format(format) === date &&
      dayjs(date, format).format(format) <= dayjs().format(format)
    )
  }

  /** 完了ボタン押下時のイベント */
  const handleComplete = React.useCallback(() => {
    // モーダル非表示
    setIsPickerOpened(false)
    const currentTime = `${String(selectedYear)}/${String(
      selectedMonth
    ).padStart(2, '0')}/${String(selectedDate).padStart(2, '0')}:${String(
      selectedHour
    ).padStart(2, '0')}`
    if (onChanged && validateCheck(currentTime, TROUBLE_DATE_FORMAT)) {
      // 変更通知
      onChanged(currentTime)
    }
  }, [selectedYear, selectedMonth, selectedDate, selectedHour, onChanged])

  /** 予約時間を切り替えたときのイベント */
  const handleHourChanged = React.useCallback(
    (value: number) => {
      // 時間変更
      setSelectedHour(value)
    },
    [selectedHour]
  )

  /** トラブル発生日時を切り替えたときのイベント */
  const handleDateChanged = React.useCallback(
    (value: number) => {
      // 時間変更
      setSelectedDate(value)
    },
    [selectedDate]
  )

  const handleYearChanged = React.useCallback(
    (value: number) => {
      setSelectedYear(value)
    },
    [selectedYear]
  )
  const handleMonthChanged = React.useCallback(
    (value: number) => {
      setSelectedMonth(value)
    },
    [selectedMonth]
  )

  // 日付を切り替えたときにTimeの選択肢を生成する
  React.useLayoutEffect(() => {
    if (minTime) {
      const currentMinYear = minYear > selectedYear ? minYear : selectedYear
      const currentMinMonth =
        minMonth > selectedMonth ? minMonth : selectedMonth
      const currentMinDate = minDate > selectedDate ? minDate : selectedDate
      const currentMinHour = minHour > selectedHour ? minHour : selectedHour

      props.onChanged &&
        props.onChanged(
          `${String(currentMinYear)}/${String(currentMinMonth).padStart(
            2,
            '0'
          )}/${String(currentMinDate).padStart(2, '0')}:${String(
            currentMinHour
          ).padStart(2, '0')}`
        )
      return
    }
    handleDateClick(false)
  }, [minTime, handleDateClick])

  // 存在しない日付をドラムロールの選択肢の表示しない
  const validateDate = (): void => {
    // 現在の年・月を取得
    const currentYearMonth = `${String(selectedYear)}/${String(
      selectedMonth
    ).padStart(2, '0')}/${String(1).padStart(2, '0')}:${String(1).padStart(
      2,
      '0'
    )}`

    // 年・月からその月の月末を割り出す
    const maxDate = dayjs(
      dayjs(currentYearMonth).endOf('month').format(TROUBLE_DATE_FORMAT)
    ).date()

    // ドラムの選択肢に月末を最大値として設定
    setDateItems(
      dateOptions.filter((x) => maxDate >= x.value && x.value >= minDate)
    )
    // ドラムの選択値が最大値異常の場合、最大値を選択値とする
    setSelectedDate(maxDate <= selectedDate ? maxDate : selectedDate)
  }

  // 未来の日付をドラムロールの選択肢に表示しない
  React.useLayoutEffect(() => {
    // 現在選択中の日付を取得
    const currentTime = `${String(selectedYear)}/${String(
      selectedMonth
    ).padStart(2, '0')}/${String(selectedDate).padStart(2, '0')}:${String(
      selectedHour
    ).padStart(2, '0')}`
    // 現在の日付を取得
    const now = dayjs().format(TROUBLE_DATE_FORMAT)

    // 各日付単位ごとに現在時刻以上かをチェック
    const isYearOver = dayjs(now).year() <= dayjs(currentTime).year()
    const isMonthOver = dayjs(now).month() <= dayjs(currentTime).month()
    const isDateOver = dayjs(now).date() <= dayjs(currentTime).date()
    const isHourOver = dayjs(now).hour() <= dayjs(currentTime).hour()

    // 年の最大値は現在の日付から取得しているためここでは処理しない

    // 年が現在の年以上の場合、月の選択肢の最大値を現在の月とする
    setMonthItems(
      monthOptions.filter((x) =>
        isYearOver
          ? Number(dayjs(now).month() + 1) >= x.value && x.value >= minMonth
          : x.value >= minMonth
      )
    )

    // 年月が現在の年月以上の場合、月の選択値を現在の月とする
    setSelectedMonth(
      isYearOver && isMonthOver
        ? dayjs(now).month() + 1
        : selectedMonth <= minMonth
        ? minMonth
        : selectedMonth
    )
    // 年月日が現在の年月日以上の場合、日の選択値を現在の日とする
    // 上記に該当しない場合は日付の存在チェックを行う
    isYearOver && isMonthOver && isDateOver
      ? setSelectedDate(dayjs(now).date())
      : validateDate()

    // 年月が現在の年月以上の場合、日の選択肢の最大値を現在の日とする
    // 上記に該当しない場合は日付の存在チェックを行う
    isYearOver && isMonthOver
      ? setDateItems(
          dateOptions.filter(
            (x) => Number(dayjs(now).date()) >= x.value && x.value >= minDate
          )
        )
      : validateDate()
    // 年月日が現在の年月日以上の場合、時刻の選択肢の最大値を現在の時刻とする
    setHourItems(
      hourOptions.filter((x) =>
        isYearOver && isMonthOver && isDateOver
          ? Number(dayjs(now).hour()) >= x.value && x.value >= minHour
          : x.value >= minHour
      )
    )
    // 年月日時刻が現在の年月日時刻以上の場合、時刻の選択値を現在の時刻とする
    setSelectedHour(
      isYearOver && isMonthOver && isDateOver && isHourOver
        ? dayjs(now).hour()
        : selectedHour <= minHour
        ? minHour
        : selectedHour
    )
  }, [selectedYear, selectedMonth, selectedDate, selectedHour, onChanged])

  return (
    <>
      <TimeContainer
        onClick={(): void => {
          handleDateClick(true)
        }}
      >
        {dayjs(time).format(TROUBLE_DATE_DISPLAY_FORMAT)}
      </TimeContainer>
      <AnimationModal
        animationType="transparent"
        on={isPickerOpened}
        type="manual"
        manualBottom="0px"
        scrollLock={true}
      >
        <ModalContainer>
          <ModalButtonContainer>
            <ModalButton onClick={handleComplete}>完了</ModalButton>
          </ModalButtonContainer>
          <TimePickerContainer>
            {
              <>
                <DrumPicker
                  options={yearItems}
                  selectedValue={selectedYear}
                  height={280}
                  onValueChanged={handleYearChanged}
                  width={34}
                />
                <DrumPicker
                  options={monthItems}
                  selectedValue={selectedMonth}
                  height={280}
                  onValueChanged={handleMonthChanged}
                  width={22}
                />
                <DrumPicker
                  options={dateItems}
                  selectedValue={selectedDate}
                  height={280}
                  onValueChanged={handleDateChanged}
                  width={22}
                />
                <DrumPicker
                  options={hourItems}
                  selectedValue={selectedHour}
                  height={280}
                  onValueChanged={handleHourChanged}
                  width={22}
                />
              </>
            }
          </TimePickerContainer>
        </ModalContainer>
      </AnimationModal>
    </>
  )
}

export default React.memo(DatePicker)

/** ルートのスタイル定義 */
const TimeContainer = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  height: 30px;
  margin-right: 12px;
  margin-left: 16px;
  padding: 0px 10px;
  font-weight: bold;
  font-size: 16px;
  border: 1px solid #707070;
`

/** モーダルのスタイル定義(ルート) */
const ModalContainer = styled.div`
  display: flex;
  flex-direction: column;
  background-color: #ffffff;
`

/** 完了ボタンのスタイル定義(ルート) */
const ModalButtonContainer = styled.div`
  display: flex;
  justify-content: flex-end;
  height: 23px;
  border: #707070 1px solid;
`

/** 完了ボタンのスタイル定義 */
const ModalButton = styled.div`
  display: flex;
  align-items: center;
  padding: 12px 8px;
  color: #1492e6;
  font-weight: bold;
  cursor: pointer;
`

/** 時/分を入力するエリアのスタイル定義 */
const TimePickerContainer = styled.div`
  display: flex;
  flex: 1;
  padding: 16px 0px;
  background: #abb0ba;
  border: #707070 1px solid;
`
